import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DIRECTUS_INSTANCE } from '../../api';
import reisloLogo from '../../img/logo-prose-captain.svg';
import backgroundImage from '../../img/bg-login-app.jpg';
import { Snackbar } from '../../ui/atoms';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('error');
  const turnstileContainerRef = useRef(null);

  useEffect(() => {
    // Skip Turnstile on localhost
    if (window.location.hostname === 'localhost') {
      setTurnstileToken('dummy-token-for-localhost');
      return;
    }

    // Clear any existing Turnstile instances
    if (turnstileContainerRef.current) {
      turnstileContainerRef.current.innerHTML = '';
    }

    // Initialize Turnstile
    if (window.turnstile) {
      window.turnstile.render(turnstileContainerRef.current, {
        sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
        callback: function(token) {
          setTurnstileToken(token);
        },
        'theme': 'light',
        'refresh-expired': 'auto'
      });
    }

    // Copy ref to variable for cleanup
    const container = turnstileContainerRef.current;
    return () => {
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setShowSnackbar(false);

    try {
      if (!turnstileToken) {
        throw new Error('Please complete the security check');
      }

      // Call to Directus forgot password endpoint (to be created in flows)
      const response = await axios.post(`${DIRECTUS_INSTANCE}/auth/password/request`, {
        email: email,
        reset_url: `${window.location.origin}/reset-password`
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status >= 200 && response.status < 300) {
        setIsSubmitted(true);
        setSnackbarMessage('Password reset instructions have been sent to your email address.');
        setSnackbarType('success');
        setShowSnackbar(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.errors?.[0]?.message || 
                          error.message || 
                          'Unable to process password reset request. Please try again.';
      
      setTimeout(() => {
        setSnackbarMessage(errorMessage);
        setSnackbarType('error');
        setShowSnackbar(true);
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const SuccessScreen = () => (
    <div className="text-center">
      <div className="mb-8">
        <svg className="mx-auto h-16 w-16 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 3.26a2 2 0 001.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      <h2 className="text-2xl font-medium text-gray-900 mb-4">Check your email</h2>
      <p className="text-base text-gray-600 mb-8">
        We've sent password reset instructions to <strong>{email}</strong>
      </p>
      <p className="text-sm text-gray-500 mb-8">
        Didn't receive the email? Check your spam folder or{' '}
        <button
          onClick={() => {
            setIsSubmitted(false);
            setEmail('');
          }}
          className="text-primary-600 hover:text-primary-500 font-semibold"
        >
          try again
        </button>
      </p>
      <a
        href="/signin"
        className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-500"
      >
        Back to Sign In
      </a>
    </div>
  );

  return (
    <div className="relative min-h-screen h-screen flex items-center justify-center px-4 py-12 sm:px-6">
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          alt=""
          src={backgroundImage}
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
        />
      </div>

      {/* Centered Form Container */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl px-8 py-6">
          {isSubmitted ? (
            <SuccessScreen />
          ) : (
            <>
              <div>
                <img
                  className="h-10 w-auto hidden"
                  src={reisloLogo}
                  alt="Prose Captain logo"
                />
                <h2 className="mt-8 text-2xl font-medium text-gray-900">
                  Forgot your password?
                </h2>
                <p className="mt-2 text-base text-gray-500">
                  Enter your email address and we'll send you instructions to reset your password.
                </p>
              </div>

              <div className="mt-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                      Email address
                    </label>
                    <div className="mt-2">
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="form-input-inset"
                        placeholder="Enter your email address"
                      />
                    </div>
                  </div>

                  {/* Turnstile container */}
                  <div ref={turnstileContainerRef} className="flex justify-center"></div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading || !turnstileToken || !email.trim()}
                      className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm ${
                        !turnstileToken || !email.trim() || loading
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-primary-600 hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                      }`}
                    >
                      {loading ? (
                        <>
                          <svg aria-hidden="true" className="w-4 h-4 mr-2 text-gray-200 animate-spin fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                          </svg>
                          Sending...
                        </>
                      ) : (
                        !turnstileToken ? 'Complete Security Check' : 'Send Reset Instructions'
                      )}
                    </button>
                  </div>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-gray-500">
                    Remember your password?{' '}
                    <a href="/signin" className="font-semibold text-primary-600 hover:text-primary-500">
                      Back to Sign In
                    </a>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
        />
      )}
    </div>
  );
};

export default ForgotPassword; 